import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageOutlined, RobotOutlined, SearchOutlined } from '@ant-design/icons';

import { boDau } from '../../../components/layout/nav-search';
import { PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Input, Skeleton } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { ChonKieuHien, useKieuHien } from '../components/chung/KieuHien';
import { DaiLoi } from '../components/chung/VungDuLieu';
import { KHOA_HIEN, QUYEN } from '../constants';
import { useBotDungDuoc } from '../hooks/useChung';
import type { ChatbotDungDuoc } from '../types';

const SO_THE_CHO = 6;
const LUOI = 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

/** Đường GỐC tới màn trò chuyện với một bot. */
export const duongBot = (id: string) => `/tro-ly-ai/tro-chuyen/${encodeURIComponent(id)}`;

function BieuTuongBot({ nho }: { nho?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600',
        nho ? 'size-7' : 'mt-0.5 size-8',
      )}
    >
      <MessageOutlined />
    </span>
  );
}

function NhanBot({ bot }: { bot: ChatbotDungDuoc }) {
  const { t, locale } = useT();
  return (
    <>
      {/* Kênh giao tiếp là PHÂN LOẠI, không phải trạng thái — nhóm biến thể phân loại. */}
      <Badge variant={bot.cheDoGiaoTiep === 'van_ban' ? 'neutral' : 'info'}>{t(`troLyAiHoiKenh.${bot.cheDoGiaoTiep}`)}</Badge>
      <span className="tabular-nums text-content-muted">
        {t('troLyAiHoiLuoiBot.demNguon', { n: soVN(bot.knowledgeBaseIds.length + bot.sourceIds.length, locale) })}
      </span>
    </>
  );
}

function TheBot({ bot, href }: { bot: ChatbotDungDuoc; href: string }) {
  return (
    <Link
      to={href}
      title={bot.name}
      className="group flex flex-col gap-2 rounded-lg border border-default bg-surface p-4 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50"
    >
      <div className="flex items-start gap-2.5">
        <BieuTuongBot />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium text-content-primary group-hover:text-primary-700">{bot.name}</p>
          <p className="line-clamp-2 text-xs text-content-muted">{bot.shortDescription || bot.purpose}</p>
        </div>
      </div>
      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1 text-xs">
        <NhanBot bot={bot} />
      </div>
    </Link>
  );
}

function DongBot({ bot, href }: { bot: ChatbotDungDuoc; href: string }) {
  return (
    <li>
      <Link
        to={href}
        title={bot.name}
        className="group flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 transition-colors hover:bg-primary-50"
      >
        <BieuTuongBot nho />
        <div className="min-w-0 flex-1 basis-56">
          <p className="truncate text-sm font-medium text-content-primary group-hover:text-primary-700">{bot.name}</p>
          <p className="truncate text-xs text-content-muted">{bot.shortDescription || bot.purpose}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 text-xs">
          <NhanBot bot={bot} />
        </div>
      </Link>
    </li>
  );
}

/**
 * Trò chuyện với bot (`/tro-ly-ai/tro-chuyen`) — chọn bot để mở cuộc trò chuyện (port `BotLuoiView.tsx` native).
 *
 * Trước đây mỗi bot là một dòng xổ ra ở thanh bên: vài chục bot là thanh bên dài hơn màn hình, tên bị cắt, không chỗ nào
 * nói bot trả lời về cái gì. Thẻ có chỗ cho mô tả và kênh giao tiếp, và có ô tìm.
 */
export function BotLuoiView() {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const q = useModulePermissions(QUYEN);
  const [tuKhoaTho, setTuKhoa] = useState('');
  const [kieu, datKieu] = useKieuHien(KHOA_HIEN.bot);
  const bots = useBotDungDuoc();

  // Chỉ bot trả lời được: bot đang bật mà không còn nguồn nào sẽ mở ra một cuộc trò chuyện không có câu trả lời.
  const ds = useMemo(() => (bots.data ?? []).filter((b) => b.active && b.answerable), [bots.data]);
  const tuKhoa = boDau(tuKhoaTho.trim());
  const loc = tuKhoa ? ds.filter((b) => boDau(`${b.name} ${b.shortDescription} ${b.purpose}`).includes(tuKhoa)) : ds;

  return (
    <div className="flex flex-col gap-3 p-4">
      <PageHeader
        breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.troChuyenVoiBot') }]}
        titleIcon={<RobotOutlined className="text-primary-600" />}
        title={t('troLyAiHoiLuoiBot.tieuDe')}
        subtitle={t('troLyAiHoiLuoiBot.phuDe')}
      />

      <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
        <div className="min-w-[220px] flex-1">
          <Input
            allowClear
            aria-label={t('troLyAiHoiLuoiBot.timBot')}
            prefix={<SearchOutlined className="text-primary-500" />}
            value={tuKhoaTho}
            onChange={(e) => setTuKhoa(e.target.value)}
            placeholder={t('troLyAiHoiLuoiBot.timBot')}
          />
        </div>
        <span className="text-xs tabular-nums text-content-muted">
          {t('troLyAiHoiLuoiBot.demBot', { shown: soVN(loc.length, locale), total: soVN(ds.length, locale) })}
        </span>
        <ChonKieuHien value={kieu} onChange={datKieu} />
      </div>

      {bots.error ? (
        <div className="rounded-lg border border-default bg-surface">
          <DaiLoi loi={bots.error} onThuLai={() => void bots.refetch()} />
        </div>
      ) : bots.isPending ? (
        <div className={LUOI} aria-busy="true">
          {Array.from({ length: SO_THE_CHO }, (_, i) => (
            <Skeleton key={i} className="h-28 rounded-lg" />
          ))}
        </div>
      ) : loc.length === 0 ? (
        <div className="rounded-lg border border-default bg-surface">
          {/* Rỗng vì ô tìm: bỏ mô tả và nút. */}
          {ds.length > 0 ? (
            <Empty title={t('troLyAiHoiLuoiBot.khongKhopTieuDe')} />
          ) : (
            <Empty
              icon={RobotOutlined}
              title={t('troLyAiHoiLuoiBot.rongTieuDe')}
              description={q.chatbotXem ? t('troLyAiHoiLuoiBot.rongMoTaQuanTri') : t('troLyAiHoiLuoiBot.rongMoTa')}
              action={
                q.chatbotXem ? (
                  <Link to={duongDan('/tro-ly-ai/chatbot')}>
                    <Button variant="primary">{t('troLyAiHoiLuoiBot.sangCauHinh')}</Button>
                  </Link>
                ) : undefined
              }
            />
          )}
        </div>
      ) : kieu === 'danh_sach' ? (
        <ul
          aria-label={t('troLyAiHoiLuoiBot.danhSachBot')}
          className="divide-y divide-default overflow-hidden rounded-lg border border-default bg-surface shadow-sm"
        >
          {loc.map((b) => (
            <DongBot key={b.id} bot={b} href={duongDan(duongBot(b.id))} />
          ))}
        </ul>
      ) : (
        <div className={LUOI}>
          {loc.map((b) => (
            <TheBot key={b.id} bot={b} href={duongDan(duongBot(b.id))} />
          ))}
        </div>
      )}
    </div>
  );
}
